import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Building2,
  FileCode2,
  Headphones,
  Radio,
  ShieldAlert,
  ShieldCheck,
  ShieldX,
  type LucideIcon,
} from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { api } from "@/lib/api.js";
import { relativeTime, formatNumber } from "@/lib/format.js";
import { StatusBadge } from "@/components/ui/StatusBadge.js";
import { Link } from "react-router-dom";

interface Stats {
  companies: { total: number; active: number };
  agents: { total: number; active: number; inactive: number };
  channels: {
    total: number;
    active: number;
    health: { ok: number; warn: number; down: number; unchecked: number };
  };
  sessions: { open: number };
}

interface Kpi {
  label: string;
  value: string | number;
  sub?: string;
  icon: LucideIcon;
  tone?: "default" | "danger" | "warn" | "success";
  to?: string;
}

function KpiCard({ kpi }: { kpi: Kpi }) {
  const toneColor =
    kpi.tone === "danger"
      ? "text-rose-600"
      : kpi.tone === "warn"
        ? "text-amber-600"
        : kpi.tone === "success"
          ? "text-emerald-600"
          : "";
  const card = (
    <div className="card p-5 flex items-start justify-between hover:border-brand-500 transition-colors">
      <div>
        <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">
          {kpi.label}
        </p>
        <p className={`text-3xl font-semibold ${toneColor}`}>
          {typeof kpi.value === "number" ? formatNumber(kpi.value) : kpi.value}
        </p>
        {kpi.sub && <p className="text-xs text-slate-500 mt-1">{kpi.sub}</p>}
      </div>
      <div className="rounded-md bg-brand-50 text-brand-600 p-2">
        <kpi.icon size={16} />
      </div>
    </div>
  );
  return kpi.to ? <Link to={kpi.to}>{card}</Link> : card;
}

export function HomePage() {
  const stats = useQuery({
    queryKey: ["dashboard", "stats"],
    queryFn: () => api.get<Stats>("/dashboard/stats"),
  });
  const activity = useQuery({
    queryKey: ["dashboard", "audit"],
    queryFn: () =>
      api.get<{
        items: {
          at: string;
          actorEmail: string | null;
          action: string;
          result: string;
        }[];
      }>("/audit?pageSize=10"),
  });

  const s = stats.data;

  const kpis: Kpi[] = [
    {
      label: "Companies activas",
      value: s ? s.companies.active : "—",
      sub: s ? `${s.companies.total} totales` : undefined,
      icon: Building2,
      to: "/companies",
    },
    {
      label: "Agentes",
      value: s ? s.agents.total : "—",
      sub: s
        ? `${s.agents.active} activos · ${s.agents.inactive} inactivos`
        : undefined,
      icon: Headphones,
      to: "/agents",
    },
    {
      label: "Canales activos",
      value: s ? s.channels.active : "—",
      sub: s ? `${s.channels.total} totales` : undefined,
      icon: Radio,
      to: "/channel-health",
    },
    {
      label: "Sesiones abiertas",
      value: s ? s.sessions.open : "—",
      sub: "Agentes online ahora",
      icon: Activity,
      to: "/sessions",
    },
  ];

  const healthKpis: Kpi[] = s
    ? [
        {
          label: "FB/IG OK",
          value: s.channels.health.ok,
          icon: ShieldCheck,
          tone: "success",
        },
        {
          label: "Por vencer",
          value: s.channels.health.warn,
          icon: ShieldAlert,
          tone: "warn",
        },
        {
          label: "Caídos",
          value: s.channels.health.down,
          icon: ShieldX,
          tone: "danger",
          to: "/channel-health",
        },
      ]
    : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inicio"
        description="Resumen operativo del backoffice. Acciones disponibles según tu rol."
      />

      <div className="grid grid-cols-4 gap-4">
        {kpis.map((k) => (
          <KpiCard key={k.label} kpi={k} />
        ))}
      </div>

      {healthKpis.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {healthKpis.map((k) => (
            <KpiCard key={k.label} kpi={k} />
          ))}
        </div>
      )}

      <section className="card">
        <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Actividad reciente del backoffice</h2>
            <p className="text-xs text-slate-500">
              Todo lo que se ha hecho desde este panel en los últimos minutos.
            </p>
          </div>
          <Link to="/audit" className="text-xs text-brand-600 hover:underline">
            Ver todo →
          </Link>
        </header>
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {activity.data?.items.length === 0 && (
            <li className="px-5 py-6 text-center text-sm text-slate-500">
              Sin actividad reciente.
            </li>
          )}
          {activity.data?.items.map((a, i) => (
            <li
              key={i}
              className="px-5 py-3 flex items-center justify-between text-sm"
            >
              <div className="flex items-center gap-3">
                <StatusBadge value={a.result} />
                <span className="mono text-xs text-slate-700 dark:text-slate-300">
                  {a.action}
                </span>
              </div>
              <div className="text-xs text-slate-500">
                {a.actorEmail ?? "sistema"} · {relativeTime(a.at)}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <p className="text-xs text-slate-400">
        <FileCode2 size={10} className="inline mr-1" />
        Los datos del core (companies, agentes, sesiones) se leen desde la réplica{" "}
        <span className="mono">bots-read</span>. Las escrituras del backoffice
        (auditoría, notas) van al master.
      </p>
    </div>
  );
}
